import { nextTick } from 'vue'
import { ZMessage } from '@zoryn/components/message'
import { hasPersistedRowId } from '../api/api-convention'
import { isGridColumnEditable, resolveGridEditable } from '../column/meta'
import { isSameRow } from '../utils/is-same-row'
import { getRowId } from '../utils/row-id'
import { resolveGridCellInput } from '../utils/resolve-grid-cell'
import type { DataGrid, GridColumnInfo, GridValidErrorItem } from '../types'

export const ROW_EDIT_BLOCKED_MESSAGE = '已有行在编辑，请先保存编辑行'

const FOCUSABLE_EDIT_TYPES = new Set([
  'input',
  'html',
  'number',
  'date',
  'datetime',
  'lov',
])

function isEmptyValue(val: unknown) {
  return val === null || val === undefined || val === ''
}

export function getValidErrorKey(rowId: string, columnId: string) {
  return `${rowId}:${columnId}`
}

function createRequiredRule(column: GridColumnInfo) {
  return {
    required: true,
    message: `${column.title || column.field}不能为空`,
  }
}

function findFirstFocusableColumn($grid: DataGrid, tableEditable: boolean) {
  return $grid.internalData.visibleColumns.find(
    (col) =>
      isGridColumnEditable(col, tableEditable) &&
      FOCUSABLE_EDIT_TYPES.has(col.type || 'base')
  )
}

async function focusRowColumnInput(
  $grid: DataGrid,
  row: Record<string, unknown>,
  column?: GridColumnInfo | null
) {
  await nextTick()
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
  if (!column?.id) {
    return
  }
  const rowId = getRowId(row)
  const bodyEl = $grid.bodyRef?.value
  if (!bodyEl) {
    return
  }
  const input = resolveGridCellInput(bodyEl, rowId, column)
  input?.focus()
}

/** 双击行进入编辑 */
export function activateRowEdit($grid: DataGrid, row: Record<string, unknown>) {
  $grid.reactData.editStore.actived = { row, field: null }
}

export function hasActiveRowEdit($grid: DataGrid) {
  const { reactData } = $grid
  return !!reactData.editStr && !!reactData.editStore.actived.row
}

export function isAnotherRowEditing(
  $grid: DataGrid,
  row: Record<string, unknown>
) {
  if (!hasActiveRowEdit($grid)) {
    return false
  }
  return !isSameRow($grid.reactData.editStore.actived.row, row)
}

function warnRowEditBlocked() {
  ZMessage.warning(ROW_EDIT_BLOCKED_MESSAGE)
}

/** 进入行编辑；若已有其他行在编辑则提示并返回 false */
export function beginRowEdit($grid: DataGrid, row: Record<string, unknown>) {
  if (isAnotherRowEditing($grid, row)) {
    warnRowEditBlocked()
    return false
  }
  $grid.reactData.editStr = hasPersistedRowId(row) ? 'edit' : 'new'
  activateRowEdit($grid, row)
  $grid.closeContextMenu?.()
  return true
}

/** 当前行是否处于行编辑态 */
export function isRowEditing($grid: DataGrid, row: Record<string, unknown>) {
  const { reactData } = $grid
  const { actived } = reactData.editStore

  if (!reactData.editStr || !actived.row) {
    return false
  }
  return isSameRow(actived.row, row)
}

export function useEdit($grid: DataGrid) {
  const { reactData, internalData } = $grid

  $grid.commitActiveEdit = () => {
    // 行内 v-model 已直接写回行数据
  }

  $grid.clearValidate = (rows, column) => {
    const rowList = rows ? (Array.isArray(rows) ? rows : [rows]) : []
    const maps = { ...reactData.validErrorMaps }

    if (!rowList.length) {
      reactData.validErrorMaps = {}
      return
    }

    if (column) {
      for (const row of rowList) {
        const key = getValidErrorKey(getRowId(row), column.id)
        delete maps[key]
      }
    } else {
      const rowIds = new Set(rowList.map((row) => getRowId(row)))
      for (const key of Object.keys(maps)) {
        if (rowIds.has(key.split(':')[0]!)) {
          delete maps[key]
        }
      }
    }
    reactData.validErrorMaps = maps
  }

  $grid.focusRowFirstInput = async (row) => {
    const tableEditable = resolveGridEditable($grid.props, $grid.options)
    const column = findFirstFocusableColumn($grid, tableEditable)
    if (!column) {
      return
    }
    reactData.editStore.actived = { row, field: column.field ?? null }
    await focusRowColumnInput($grid, row, column)
  }

  $grid.validateRows = async (rows, options) => {
    if (!rows.length) {
      return
    }

    $grid.commitActiveEdit()
    const tableEditable = resolveGridEditable($grid.props, $grid.options)
    const requiredColumns = internalData.visibleColumns.filter(
      (col) =>
        col.field && col.required && isGridColumnEditable(col, tableEditable)
    )

    if (!requiredColumns.length) {
      reactData.validErrorMaps = {}
      return
    }

    reactData.validErrorMaps = {}
    let firstError: GridValidErrorItem | null = null
    const nextMaps: Record<string, GridValidErrorItem> = {}

    for (const row of rows) {
      const rowId = getRowId(row)
      for (const column of requiredColumns) {
        const val = row[column.field!]
        if (isEmptyValue(val)) {
          const rule = createRequiredRule(column)
          const item: GridValidErrorItem = {
            row,
            column,
            content: rule.message,
          }
          nextMaps[getValidErrorKey(rowId, column.id)] = item
          if (!firstError) {
            firstError = item
          }
        }
      }
    }

    reactData.validErrorMaps = nextMaps

    if (!firstError) {
      return
    }

    await nextTick()
    await $grid.scrollToCell?.(firstError.row, firstError.column)
    reactData.editStore.actived = {
      row: firstError.row,
      field: firstError.column.field ?? null,
    }

    if (options?.throwError === false) {
      return
    }

    await focusRowColumnInput($grid, firstError.row, firstError.column)
    throw new Error(firstError.content || '请完善必填项')
  }
}
